import type { Project, ProjectImage, ProjectMetaData } from "@/types/portfolio"
import { normalizeClientLogoSrc } from "@/lib/clientLogos"

type ApiProjectImage = {
  id: string
  description: string | null
  filename: string | null
  ext: string | null
  tags: string[]
  deviceType: "desktop" | "tablet" | "mobile" | "generic" | "print" | null
}

type ApiProject = {
  id: string
  title: string | null
  datePublished: string
  projectType: string | null
  roles: string[]
  tags: string[]
  techStack?: string[]
  description: string | null
  metadata: ProjectMetaData
  images: ApiProjectImage[]
  createdAt: string
  updatedAt: string
}

const API_BASE = import.meta.env.VITE_API_BASE_URL?.replace(/\/$/, "") ?? ""
const TOKEN_QUERY_PARAM = "token"
const TOKEN_SESSION_STORAGE_KEY = "gallery_access_token"
type TokenSource = "url" | "session" | null

export class ApiRequestError extends Error {
  status: number
  path: string
  hadToken: boolean

  constructor(status: number, path: string, hadToken: boolean) {
    super(`API request failed: ${status}`)
    this.name = "ApiRequestError"
    this.status = status
    this.path = path
    this.hadToken = hadToken
  }
}

function getUrlToken(): string | null {
  if (typeof window === "undefined") return null
  const token = new URLSearchParams(window.location.search).get(TOKEN_QUERY_PARAM)
  if (!token) return null
  const trimmed = token.trim()
  return trimmed.length > 0 ? trimmed : null
}

function getSessionToken(): string | null {
  if (typeof window === "undefined") return null
  const token = window.sessionStorage.getItem(TOKEN_SESSION_STORAGE_KEY)
  if (!token) return null
  const trimmed = token.trim()
  return trimmed.length > 0 ? trimmed : null
}

function setSessionToken(token: string) {
  if (typeof window === "undefined") return
  window.sessionStorage.setItem(TOKEN_SESSION_STORAGE_KEY, token)
}

function clearSessionToken() {
  if (typeof window === "undefined") return
  window.sessionStorage.removeItem(TOKEN_SESSION_STORAGE_KEY)
}

function endpointSupportsToken(path: string): boolean {
  if (path.startsWith("/api/galleries/")) return true
  return /^\/api\/projects\/[^/?]+/.test(path)
}

function resolveToken(path: string): { token: string | null; source: TokenSource } {
  if (!endpointSupportsToken(path)) return { token: null, source: null }

  const urlToken = getUrlToken()
  if (urlToken) {
    return { token: urlToken, source: "url" }
  }

  const sessionToken = getSessionToken()
  if (sessionToken) {
    return { token: sessionToken, source: "session" }
  }

  return { token: null, source: null }
}

function withToken(path: string, token: string): string {
  const [pathname, query = ""] = path.split("?")
  const params = new URLSearchParams(query)
  params.set(TOKEN_QUERY_PARAM, token)
  const nextQuery = params.toString()
  return nextQuery ? `${pathname}?${nextQuery}` : pathname
}

function clearTokenFromBrowserUrl() {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  if (!url.searchParams.has(TOKEN_QUERY_PARAM)) return
  url.searchParams.delete(TOKEN_QUERY_PARAM)
  const nextSearch = url.searchParams.toString()
  const nextUrl = `${url.pathname}${nextSearch ? `?${nextSearch}` : ""}${url.hash}`
  window.history.replaceState(window.history.state, "", nextUrl)
}

function parseImage(image: ApiProjectImage): ProjectImage {
  return {
    id: image.id,
    description: image.description,
    filename: image.filename,
    ext: image.ext,
    tags: image.tags,
    deviceType: image.deviceType,
  }
}

function parseProject(project: ApiProject): Project {
  const metadata: ProjectMetaData = {
    ...project.metadata,
    clientLogo: normalizeClientLogoSrc(project.metadata.clientLogo),
  }

  return {
    id: project.id,
    title: project.title,
    datePublished: new Date(project.datePublished),
    projectType: project.projectType,
    roles: project.roles,
    tags: project.tags,
    techStack: project.techStack ?? [],
    description: project.description,
    metadata,
    images: project.images.map(parseImage),
    createdAt: new Date(project.createdAt),
    updatedAt: new Date(project.updatedAt),
  }
}

async function request<T>(path: string): Promise<T> {
  const { token, source } = resolveToken(path)
  const requestPath = token ? withToken(path, token) : path

  const response = await fetch(`${API_BASE}${requestPath}`, {
    credentials: "omit",
    cache: "no-store",
  })
  if (!response.ok) {
    if (response.status === 401 && source === "session") {
      clearSessionToken()
    }
    throw new ApiRequestError(response.status, requestPath, Boolean(token))
  }

  if (source === "url" && token) {
    setSessionToken(token)
    clearTokenFromBrowserUrl()
  }

  return response.json() as Promise<T>
}

export async function getProjects(setSlug: string = "default"): Promise<Project[]> {
  if (setSlug !== "default") {
    const data = await request<{ projects: ApiProject[] }>(`/api/galleries/${encodeURIComponent(setSlug)}/projects`)
    return data.projects.map(parseProject)
  }

  const data = await request<{ projects: ApiProject[] }>("/api/projects")
  return data.projects.map(parseProject)
}

export async function getProjectById(id: string): Promise<Project | null> {
  try {
    const data = await request<{ project: ApiProject }>(`/api/projects/${id}`)
    return parseProject(data.project)
  } catch (error) {
    if (error instanceof ApiRequestError && error.status === 404) {
      return null
    }
    throw error
  }
}

export async function getTags(): Promise<{ name: string; count: number }[]> {
  const data = await request<{ tags: { name: string; count: number }[] }>("/api/tags")
  return data.tags
}
